import { Component, ElementRef, EventEmitter, Input, NgZone, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TokenCard, TokenData, TokenProvider } from './token-data';
import { CreditCardValidators } from 'angular-cc-library';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CardConnectTokenResponse, HPSErrorResponse, HPSTokenResponse, NgMultiGatewayPaymentsService } from './ng-multi-gateway-payments.service';
import { HttpErrorResponse } from '@angular/common/http';
import { states } from './states';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { } from 'google.maps';

export interface ZipAddress {
  zip: string;
}

export interface FullAddress extends ZipAddress {
  address: string;
  city: string;
  state: string;
}

export interface ExtendedAddress {
  address: ZipAddress;
  email: string;
}

export interface FullExtendedAddress extends ExtendedAddress {
  address: FullAddress;
  firstName: string;
  lastName: string;
  nameOnAccount: string;
  phone: string;
}

const STYLE = `
label {
  display: inline-block;
  margin-bottom: 5px;
  font-weight: 700;
  box-sizing: border-box;
}
label, .iframe-errors {
  font-family: "Helvetica Neue",Helvetica,Arial,sans-serif;
  font-size: 14px;
}
.iframe-errors {
  border-radius: 4px;
  border: 1px solid red;
  background-color: #f88;
  padding: 10px;
  margin-bottom: 10px;
}
body{
  margin: 0px;
  box-sizing: border-box;
}
input:not([type=radio]) {
  display: block;
  height: 34px;
  padding: 6px 12px;
  font-size: 14px;
  line-height: 1.42857143;
  color: #555;
  background-color: #fff;
  background-image: none;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%);
  box-sizing: border-box;
  width: 100%;
}
input[type=radio]{
  margin-right: 4px;
}
select{
  height: 34px;
  padding: 6px 12px;
  font-size: 14px;
  line-height: 1.42857143;
  color: #555;
  background-color: #fff;
  background-image: none;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%);
  box-sizing: border-box;
  margin-bottom: 12px;
}
input.error {
  border-color: red;
}
.help-block {
    font-size: 80%;
    color: #dc3545;
  }
`;

// Keep error messages that came in in the last half second
const timedErrorDelay = 500;

interface TimedError {
  message: string;
  time: Date;
}

interface TokenMessage extends CardConnectTokenResponse {
  expiry?: string;
}
@Component({
  selector: 'mf-multi-gateway-payments',
  templateUrl: './ng-multi-gateway-payments.component.html',
  styles: [STYLE],
  standalone: false
})
export class NgMultiGatewayPaymentsComponent implements OnInit {
  @Output() token: EventEmitter<TokenData> = new EventEmitter<TokenData>();
  @Output() processing: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() validationError: EventEmitter<string> = new EventEmitter<string>();
  @Input() type: TokenProvider = TokenProvider.Heartland;
  @Input() fullAddress = false;
  @Input() internationalAddress = false; // TODO: support this
  @Input() hpsUrl = 'https://cert.api2.heartlandportico.com/Hps.Exchange.PosGateway.Hpf.v1/api/token';
  @Input() environment!: {API_URL: string; hpsV1: {publicKey: string; url: string;}; global_config_version: string};
  @Input() cardConnectUrl = 'https://boltgw-uat.cardconnect.com';
  @Input() showAcctType = true;
  @Input() showCheckToggle = false;
  @Input() showCreditCard = true;
  @Input() submitText = 'Submit Payment';
  @Input() showCancel = false;
  @Input() iframeStyle = '';
  @Input() callback = false;
  @Input() defaultAddress?: ExtendedAddress | FullExtendedAddress;
  @Input() placesAPIKey?: string;
  @ViewChild('addressInput', { static: false }) public addressInput!: ElementRef<HTMLInputElement>;

  public ccIFrameUrl!: SafeResourceUrl;
  public error = '';
  public errors: {
    number: string;
    expiration: string;
    cvc: string;
    firstName: string;
    lastName: string;
    nameOnAccount: string;
    line1: string;
    city: string;
    state: string;
    phone: string;
    zip: string;
    email: string;
  };

  public tokenData: TokenMessage | undefined;
  public submitted = false;
  public isCheck = false;
  public states: {
    name: string;
    abbr: string;
  }[];

  public iframeErrors: TimedError[];

  // Form and Properties:
  public hpsForm: FormGroup;
  public nameForm: FormGroup;
  public addressForm: FormGroup;
  public generalForm: FormGroup;
  public checkForm: FormGroup;
  public cvc: FormControl;
  public email: FormControl;
  public expiration: FormControl;
  public number: FormControl;
  public firstName: FormControl;
  public lastName: FormControl;
  public nameOnAccount: FormControl;
  public line1: FormControl;
  public city: FormControl;
  public state: FormControl;
  public phone: FormControl;
  public zip: FormControl;
  public acctTypeControl: FormControl;
  public routing: FormControl;
  public account: FormControl;
  public paymentTypeControl: FormControl;


  public cardType = '';
  public cardNumberMask = '0000 0000 0000 0000';
  public cvcMask = '000';

  constructor(
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private tokenService: NgMultiGatewayPaymentsService,
    protected ngZone: NgZone
  ) {
    this.errors = {
      cvc: '',
      email: '',
      expiration: '',
      number: '',
      firstName: '',
      lastName: '',
      nameOnAccount: '',
      line1: '',
      city: '',
      state: '',
      phone: '',
      zip: ''
    };
    this.iframeErrors = [];
    this.states = states;

    const fullDefaultAddress: FullExtendedAddress = {
      email: '',
      firstName: '',
      lastName: '',
      nameOnAccount: '',
      phone: '',
      ...this.defaultAddress,
      address: {
        address: '',
        city: '',
        state: '',
        zip: '',
        ...this.defaultAddress?.address
      }
    };

    this.cvc = new FormControl('', [Validators.required, Validators.minLength(3), Validators.maxLength(4)]);
    this.email = new FormControl(fullDefaultAddress.email || '', [Validators.required, Validators.email]);
    this.expiration = new FormControl('', [Validators.required, CreditCardValidators.validateExpDate]);
    this.number = new FormControl('', [Validators.required, CreditCardValidators.validateCCNumber]);
    this.zip = new FormControl(fullDefaultAddress.address.zip, [Validators.required, Validators.minLength(5)]);
    this.firstName = new FormControl(fullDefaultAddress.firstName);
    this.lastName = new FormControl(fullDefaultAddress.lastName);
    this.nameOnAccount = new FormControl(fullDefaultAddress.nameOnAccount);
    this.line1 = new FormControl(fullDefaultAddress.address.address, [Validators.required]);
    this.city = new FormControl(fullDefaultAddress.address.city, [Validators.required]);
    this.state = new FormControl(fullDefaultAddress.address.state, [Validators.required]);
    this.phone = new FormControl(fullDefaultAddress.phone, [Validators.required]);
    this.acctTypeControl = new FormControl(null, [control => {
      if (!this.showAcctType || !(control.value == null || control.value.length === 0)) {
        return null;
      } else {
        return {
          required: true
        };
      }
    }]);
    this.routing = new FormControl('', [Validators.required, Validators.minLength(9), Validators.maxLength(9)]);
    this.account = new FormControl('', [Validators.required, Validators.minLength(4), Validators.maxLength(17)]);
    this.paymentTypeControl = new FormControl(false); // false = credit card, true = echeck

    this.hpsForm = this.fb.group({
      cvc: this.cvc,
      expiration: this.expiration,
      number: this.number
    });

    this.nameForm = this.fb.group({
      firstName: this.firstName,
      lastName: this.lastName,
      nameOnAccount: this.nameOnAccount
    });

    this.addressForm = this.fb.group({
      line1: this.line1,
      city: this.city,
      state: this.state,
      phone: this.phone
    });

    this.generalForm = this.fb.group({
      email: this.email,
      zip: this.zip,
      paymentType: this.paymentTypeControl
    });

    this.checkForm = this.fb.group({
      routing: this.routing,
      account: this.account,
      acctType: this.acctTypeControl
    });

    // Subscribe to payment type changes to update isCheck
    this.paymentTypeControl.valueChanges.subscribe(value => {
      this.isCheck = value;
    });

    /**
     * Listen for the CardConnect form to complete tokenization
     */
    window.addEventListener('message', (event) => {
      if (event.origin === this.cardConnectUrl) {
        this.handleTokenResponse(event);
      }
    }, false);
  }

  public handleTokenResponse(event: MessageEvent<string>): void {
    const data = JSON.parse(event.data);
    const now = new Date();
    if (data.validationError) {
      this.validationError.emit(data.validationError);
      // Remove any that are duplicates of the new error or that are too old
      this.iframeErrors = this.iframeErrors.filter(error => {
        return error.message !== data.validationError && (now.getTime() - error.time.getTime() < timedErrorDelay);
      });
      this.iframeErrors.push({
        message: data.validationError,
        time: now
      });
    } else if (data.message) {
      // If we got a token, there should be no errors
      this.iframeErrors = [];
      this.tokenData = data;
    }
  }

  public setCCIFrameUrl() {
    const query = new URLSearchParams({
      useexpiry: 'true',
      usecvv: 'true',
      invalidcreditcardevent: 'true',
      invalidcvvevent: 'true',
      invalidexpiryevent: 'true',
      css: this.iFrameCss()
    });

    this.ccIFrameUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.cardConnectUrl + '/itoke/ajax-tokenizer.html?' + query.toString());
  }

  public importGoogleMapsScript() {
    const scripts = document.querySelectorAll(`[src^="https://maps.googleapis.com/maps/api/js"]`);
    if (scripts.length) {
      return Promise.resolve(google.maps);
    } else {
      const script = document.createElement('script')
      script.type = 'text/javascript';
      script.async = true;
      script.defer = true;
      const callbackName = `GooglePlaces_cb_` + ((Math.random() * 1e9) >>> 0);
      const query = new URLSearchParams({
        v: '3',
        callback: callbackName,
        key: this.placesAPIKey || '',
        libraries: 'places',
        language: 'en'
      });
      script.src = `https://maps.googleapis.com/maps/api/js?${query.toString()}`;

      const readyPromise = new Promise((resolve, reject) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any)[callbackName] = () => {
          resolve(google.maps);
        };
        script.onerror = reject;
      });
      document.body.appendChild(script);
      return readyPromise;
    }
  }

  public ngOnInit() {
    if (!this.showCreditCard) {
      // Only eCheck is offered, so lock the form to it.
      this.paymentTypeControl.setValue(true);
    }
    this.setCCIFrameUrl();
    if (this.placesAPIKey) {
      this.importGoogleMapsScript().then(() => {
        const input = new google.maps.places.Autocomplete(this.addressInput.nativeElement, {
          fields: ['address_components'],
          strictBounds: false,
          types: ['address'],
          componentRestrictions: { country: ['us'] }
        });
        input.addListener('place_changed', () => {
          const places = input.getPlace();
          this.ngZone.run(() => {
            const newAddressValue = {
              ...this.addressForm.value
            };

            let addressLine = '';
            let postalCode = '';
            places.address_components?.forEach(component => {
              component.types.forEach(type => {
                // eslint-disable-next-line default-case
                switch (type) {
                  case 'street_number': addressLine = `${component.long_name} ${addressLine}`; break;
                  case 'route': addressLine += component.short_name; break;
                  case "postal_code": postalCode = `${component.long_name}${postalCode}`; break;
                  case "postal_code_suffix": postalCode = `${postalCode}-${component.long_name}`; break;
                  case "locality": newAddressValue.city = component.long_name; break;
                  case "administrative_area_level_1": newAddressValue.state = component.short_name; break;
                }
              })
            });
            newAddressValue.line1 = addressLine;
            this.generalForm.setValue({
              ...this.generalForm.value,
              zip: postalCode
            });
            this.addressForm.setValue(newAddressValue);
          });
        });
      });
    }
  }

  detectCardType(cardNumber: string): string {
    const cleanNumber = cardNumber.replace(/\D/g, '');

    if (cleanNumber.match(/^4/)) {
      return 'visa';
    } else if (cleanNumber.match(/^5[1-5]/) || cleanNumber.match(/^2[2-7]/)) {
      return 'mastercard';
    } else if (cleanNumber.match(/^3[47]/)) {
      return 'amex';
    } else if (cleanNumber.match(/^6(?:011|5|4[4-9])/)) {
      return 'discover';
    } else if (cleanNumber.match(/^35/)) {
      return 'jcb';
    } else if (cleanNumber.match(/^62/)) {
      return 'unionpay';
    } else if (cleanNumber.match(/^3[0689]/)) {
      return 'diners';
    } else if (cleanNumber.match(/^(?:5[0678]|6[0-9])/)) {
      return 'maestro';
    }

    return 'unknown';
  }

  onCardNumberChange(event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }
    const cardNumber = event.target.value;
    this.cardType = this.detectCardType(cardNumber);

    switch (this.cardType) {
      case 'amex':
      case 'diners':
        this.cardNumberMask = '0000 000000 00000';
        this.cvcMask = '0000';
        break;
      case 'unionpay':
        this.cardNumberMask = '0000 0000 0000 0000 0000';
        this.cvcMask = '000';
        break;
      default:
        this.cardNumberMask = '0000 0000 0000 0000';
        this.cvcMask = '000';
        break;
    }
  }

  public cancelForm() {
    this.hpsForm.reset();
    this.generalForm.reset();
    this.checkForm.reset();
    this.paymentTypeControl.setValue(!this.showCreditCard);
    this.setCCIFrameUrl();
  }

  public async processForm() {
    this.markProcessing(true);
    let expYear: string;
    let expMonth: string;
    let last4: string;
    let token: string;
    let invalidForm = false;

    if (this.isCheck === false) {
      this.nameForm.controls['nameOnAccount'].clearValidators();
      this.nameForm.controls['firstName'].setValidators(Validators.required);
      this.nameForm.controls['lastName'].setValidators(Validators.required);
    } else {
      this.nameForm.controls['firstName'].clearValidators();
      this.nameForm.controls['lastName'].clearValidators();
      this.nameForm.controls['nameOnAccount'].setValidators(Validators.required);
    }

    this.nameForm.controls['firstName'].updateValueAndValidity();
    this.nameForm.controls['lastName'].updateValueAndValidity();
    this.nameForm.controls['nameOnAccount'].updateValueAndValidity();

    if (this.generalForm.invalid) {
      Object.keys(this.generalForm.controls).forEach((key: string) => {
        if (this.generalForm.controls[key].invalid) {
          // Mark as dirty so the error shows.
          this.generalForm.controls[key].markAsDirty();
        }
      });
      invalidForm = true;
    }
    if (this.fullAddress) {
      if (this.nameForm.invalid) {
        Object.keys(this.nameForm.controls).forEach((key: string) => {
          if (this.nameForm.controls[key].invalid) {
            // Mark as dirty so the error shows.
            this.nameForm.controls[key].markAsDirty();
          }
        });
        invalidForm = true;
      }
      if (this.addressForm.invalid) {
        Object.keys(this.addressForm.controls).forEach((key: string) => {
          if (this.addressForm.controls[key].invalid) {
            // Mark as dirty so the error shows.
            this.addressForm.controls[key].markAsDirty();
          }
        });
        invalidForm = true;
      }
    }
    if (this.type === TokenProvider.Heartland && this.hpsForm.invalid) {
      Object.keys(this.hpsForm.controls).forEach((key: string) => {
        if (this.hpsForm.controls[key].invalid) {
          // Mark as dirty so the error shows.
          this.hpsForm.controls[key].markAsDirty();
        }
      });
      invalidForm = true;
    }
    if (this.type === TokenProvider.CardConnect && this.isCheck && this.checkForm.invalid) {
      Object.keys(this.checkForm.controls).forEach((key: string) => {
        if (this.checkForm.controls[key].invalid) {
          // Mark as dirty so the error shows.
          this.checkForm.controls[key].markAsDirty();
        }
      });
      invalidForm = true;
    }
    if (invalidForm) {
      this.error = 'Form contains errors';
      this.markProcessing(false);
      return;
    }
    let customerData: TokenData['customer'] = {
      address: {
        zip: this.generalForm.controls['zip'].value,
      },
      email: this.generalForm.controls['email'].value,
    };
    if (this.fullAddress) {
      customerData = {
        ...customerData,
        address: {
          ...customerData.address,
          address: this.addressForm.controls['line1'].value,
          city: this.addressForm.controls['city'].value,
          state: this.addressForm.controls['state'].value
        },
        phone: this.addressForm.controls['phone'].value
      };
      if (this.isCheck) {
        customerData.nameOnAccount = this.nameForm.controls['nameOnAccount'].value;
      } else {
        customerData.firstName = this.nameForm.controls['firstName'].value;
        customerData.lastName = this.nameForm.controls['lastName'].value;
      }
    }
    if (this.type == TokenProvider.CardConnect) {
      if (this.isCheck) {
        this.tokenData = await this.tokenService.cardConnectTokenize(this.cardConnectUrl, {
          account: this.checkForm.controls['routing'].value + '/' + this.checkForm.controls['account'].value
        });
      }
      if (this.tokenData == undefined) {
        this.error = 'Could not process payment information. Try entering again.'
        this.markProcessing(false);
        return;
      }
      let card: TokenCard | undefined;
      if (this.isCheck) {
        token = this.tokenData.token;
        // A bank account has no card number, so its last 4 digits stand in as
        // last4 — consumers need it to identify the saved method in a list.
        card = {
          last4: this.checkForm.controls['account'].value.slice(-4)
        };
        if (this.checkForm.controls['acctType'].value !== null) {
          const split = this.checkForm.controls['acctType'].value.split('|')
          card.accttype = split[0];
          card.business = split[1];
        }
      } else {
        card = {
          expYear: this.tokenData.expiry?.substr(0, 4),
          expMonth: this.tokenData.expiry?.substr(4).padStart(2, '0'),
          last4: this.tokenData.message.substr(-4)
        }
        token = this.tokenData.message;
      }

      const payment = new TokenData(
        this.type,
        token,
        customerData,
        card,
        this.callback ? () => {
          this.markProcessing(false);
        } : undefined
      );

      this.token.emit(payment);
      if (!this.callback) {
        this.markProcessing(false);
      }
    } else if (this.type === TokenProvider.Fake) {
      const payment = new TokenData(
        this.type,
        'fake',
        customerData,
        undefined,
        this.callback ? () => {
          this.markProcessing(false);
        } : undefined
      );
      this.token.emit(payment);
      if (!this.callback) {
        this.markProcessing(false);
      }
    } else {
      const exp: string[] = this.hpsForm.controls['expiration'].value.split('/');
      expMonth = exp[0].trim();
      expYear = exp[1].trim();
      if (expYear.length == 2) {
        expYear = '20' + expYear;
      }
      last4 = this.hpsForm.controls['number'].value.substr(-4);
      this.tokenService.hpsTokenize(
        this.environment,
        this.hpsForm.controls['number'].value.trim().replace(/\s+/g, ''),
        expMonth, expYear,
        this.hpsForm.controls['cvc'].value.trim(),
      ).subscribe({
        next: (response: HPSTokenResponse) => {
          if (!response) {
            this.error = 'Error submitting payment, please try again';
            this.markProcessing(false);
          } else if (response.object === 'token') {
            const payment = new TokenData(
              this.type,
              response.token_value,
              customerData,
              { expMonth, expYear, last4 },
              this.callback ? () => {
                this.markProcessing(false);
              } : undefined
            );
            this.token.emit(payment);
            if (!this.callback) {
              this.markProcessing(false);
            }
          }
        },
        error: (response: HttpErrorResponse) => {
          const error = response.error as HPSErrorResponse;
          if (error.param) {
            const [, field] = error.param.split('.');
            switch (field) {
              case 'number':
                this.number.setErrors({ hps: true });
                this.errors.number = error.message;
                break;
              case 'exp_month':
              case 'exp_year':
                this.expiration.setErrors({ hps: true });
                this.errors.expiration = error.message;
                break;
              case 'cvc':
                this.cvc.setErrors({ hps: true });
                this.errors.cvc = error.message;
                break;
              default:
                this.error = error.message;
            }
          } else {
            this.error = error.message;
          }
          this.markProcessing(false);
        }
      });
    }
  }

  private iFrameCss() {
    return STYLE + '\n' + this.iframeStyle;
  }

  private markProcessing(flag: boolean): void {
    this.submitted = flag;
    this.processing.emit(flag);
  }
}
